import React, { useEffect } from 'react';
import { Stack, Typography, Button, CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSettingsUI, useSystemId } from '../../../hooks/useReduxSelectors';
import { sendCommand } from '../../../ducks/uiDuck';
import { fetchSettingsAuth } from '../../../ducks/settingsDuck';
import useRoutineStartStopButtonWithCounter from '../../../helpers/useRoutineStartStopButtonWithCounter';

// Manual "Start"/"Stop" run controls for the AutoZero/QAL3 routines. Extracted out of
// ReportSearchBar (which was getting crowded) so each report's own Accordion header can
// host its own manual-run controls instead.
const ManualRunControls = ({ reportName }) => {
  const systemId = useSystemId();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const settingsUI = useSettingsUI();
  const disableStartButton = reportName === 'AutoZero'
    ? settingsUI?.autoZero?.isRunManuallyBtnDisabled
    : settingsUI?.qal3?.isRunManuallyBtnDisabled;

  const settingKey = reportName === 'AutoZero' ? 'AutoZeroSettings' : 'QAL3Settings';
  const reportSettings = useSelector(
    ({ config }) =>
      config[systemId].settings[settingKey]
      &&
      config[systemId].settings[settingKey].formattedEntries
      &&
      config[systemId].settings[settingKey].formattedEntries.jsonData
  );

  useEffect(() => {
    dispatch(fetchSettingsAuth({ systemId, args: { setting: settingKey, stateName: settingKey } }));
  }, [dispatch, systemId, settingKey]);

  const runManualTest = (buttonCommand) => {
    if (buttonCommand === 'Start') {
      const payload = {
        argsProps: {
          HwId: 'EdgeDevice',
          Command: reportName,
          Value: buttonCommand,
        },
        commandType: reportName,
        systemId,
      };
      dispatch(sendCommand(payload));
    }
  };

  const { startCounter, stopCounter } = useRoutineStartStopButtonWithCounter(systemId, dispatch, sendCommand, reportName);

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      onClick={(e) => e.stopPropagation()}
    >
      <Typography variant="caption" sx={{ lineHeight: 1.2, textAlign: 'center', mr: 0.5 }}>
        {t('Manual')}<br />{t('Run')}
      </Typography>
      <Button
        disabled={disableStartButton || !(reportSettings && reportSettings.inUse)}
        size="small"
        onClick={() => runManualTest('Start')}
        variant="contained"
        color="success"
      >
        {t('Start')}
        {disableStartButton && (<CircularProgress size={14} sx={{ ml: 0.5 }} />)}
      </Button>
      <Button
        disabled={!disableStartButton}
        size="small"
        onMouseDown={startCounter}
        onMouseUp={stopCounter}
        onMouseLeave={stopCounter}
        onTouchStart={startCounter}
        onTouchEnd={stopCounter}
        onTouchCancel={stopCounter}
        variant="contained"
        color="error"
      >
        {t('Stop')}
      </Button>
    </Stack>
  );
};

export default ManualRunControls;
